/** For SVG attribute values. */
export function escapeXmlAttr(s: string): string {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/"/g, "&quot;");
}

/** `n-1` boundaries between `n` equal luminance buckets (dark → light). */
export function equalSplitThresholds(numColors: number): number[] {
    if (numColors <= 1) return [];
    return Array.from(
        { length: numColors - 1 },
        (_, i) => (i + 1) / numColors
    );
}

const MIN_THRESHOLD_GAP = 0.006;

/** Keep threshold order and a minimum gap between adjacent boundaries. */
export function clampThresholdDrag(
    thresholds: number[],
    index: number,
    value: number,
    minGap = MIN_THRESHOLD_GAP
): number[] {
    const next = [...thresholds];
    const low = index === 0 ? minGap : thresholds[index - 1] + minGap;
    const high =
        index === thresholds.length - 1
            ? 1 - minGap
            : thresholds[index + 1] - minGap;
    next[index] = Math.max(low, Math.min(high, value));
    return next;
}

/** CSS `linear-gradient` for a piecewise-constant palette along luminance. */
export function buildPaletteGradientCss(
    colors: string[],
    thresholds: number[]
): string {
    const n = colors.length;
    if (n === 0) return "#666666";
    if (n === 1) return colors[0];
    const th =
        thresholds.length === n - 1
            ? thresholds
            : equalSplitThresholds(n);
    const parts: string[] = [];
    parts.push(`${colors[0]} 0%`);
    for (let i = 0; i < th.length; i++) {
        const p = th[i] * 100;
        parts.push(`${colors[i]} ${p}%`);
        parts.push(`${colors[i + 1]} ${p}%`);
    }
    parts.push(`${colors[n - 1]} 100%`);
    return `linear-gradient(to right, ${parts.join(", ")})`;
}

/**
 * Map luminance in [0,1] to palette index using ordered thresholds.
 * `thresholds[i]` is the boundary between `palette[i]` (below) and `palette[i+1]` (at and above).
 */
export function luminanceToPaletteIndex(
    l: number,
    palette: string[],
    thresholds?: number[] | null
): number {
    const n = palette.length;
    if (n <= 1) return 0;
    const t = Math.max(0, Math.min(1, l));
    const th =
        thresholds && thresholds.length === n - 1
            ? thresholds
            : equalSplitThresholds(n);
    let idx = 0;
    for (let i = 0; i < th.length; i++) {
        if (t < th[i]) break;
        idx++;
    }
    return Math.min(idx, n - 1);
}

/** Luminance at the center of palette bucket `index` (for blob masking after quantization). */
export function paletteBucketMidpoint(
    index: number,
    numColors: number,
    thresholds: number[]
): number {
    if (numColors <= 1) return 0.5;
    const th =
        thresholds.length === numColors - 1
            ? thresholds
            : equalSplitThresholds(numColors);
    const lo = index === 0 ? 0 : th[index - 1];
    const hi = index === numColors - 1 ? 1 : th[index];
    return (lo + hi) / 2;
}

export function luminanceToPaletteFill(
    l: number,
    palette: string[],
    thresholds?: number[] | null
): string {
    const n = palette.length;
    if (n === 0) {
        const v = Math.round(Math.max(0, Math.min(1, l)) * 255);
        return `rgb(${v},${v},${v})`;
    }
    if (n === 1) {
        return palette[0];
    }
    const idx = luminanceToPaletteIndex(l, palette, thresholds);
    return palette[idx];
}

export function parsePaletteInput(text: string): string[] {
    return text
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

export const DEFAULT_LUMINANCE_PALETTE = [
    "#0d0d0d",
    "#4a3728",
    "#8b6914",
    "#c9a227",
    "#e8d5b7",
    "#faf6f0",
];

export function findMatchingPalettePresetId(colors: string[]): string {
    for (const p of LUMINANCE_PALETTE_PRESETS) {
        if (
            p.colors.length === colors.length &&
            p.colors.every((c, i) => c === colors[i])
        ) {
            return p.id;
        }
    }
    return "custom";
}

export const LUMINANCE_PALETTE_PRESETS: {
    id: string;
    label: string;
    colors: string[];
}[] = [
    {
        id: "grayscale",
        label: "Grayscale",
        colors: ["#000000", "#333333", "#666666", "#999999", "#cccccc", "#ffffff"],
    },
    {
        id: "earth",
        label: "Earth",
        colors: [...DEFAULT_LUMINANCE_PALETTE],
    },
    {
        id: "heat",
        label: "Heat",
        colors: ["#0c1024", "#3b0f55", "#8c1f5f", "#d6512a", "#f7b538", "#fff1c8"],
    },
    {
        id: "ocean",
        label: "Ocean",
        colors: ["#031a3a", "#084c7c", "#0f7ba4", "#4ecdc4", "#95e1d3", "#e8f9f6"],
    },
    {
        id: "gameboy",
        label: "Game Boy",
        colors: ["#0f380f", "#306230", "#8bac0f", "#9bbc0f", "#c4cfa1"],
    },
    {
        id: "duotone",
        label: "Duotone",
        colors: ["#1a0b2e", "#4c1d95", "#7c3aed", "#c084fc", "#f5e1ff"],
    },
];
